import type { Filter, FilterDataMultiSelect, FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { AccommodationMealPlan } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { counterOf, disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';

const MEAL_PLAN_DEFAULT_VALUE = '0';

type DefaultMealPlan = {
  value: string;
  translationKey: string;
  isDefault?: boolean;
};
const defaultMealPlans: DefaultMealPlan[] = [
  {
    value: MEAL_PLAN_DEFAULT_VALUE,
    translationKey: 'vacation.oss.components.searchfilters.filters.meal.any',
    isDefault: true,
  },
  { value: '1', translationKey: 'vacation.oss.components.searchresults.filterTags.meal.1' },
  { value: '2', translationKey: 'vacation.oss.components.searchresults.filterTags.meal.2' },
  { value: '3', translationKey: 'vacation.oss.components.searchresults.filterTags.meal.3' },
  { value: '4', translationKey: 'vacation.oss.components.searchresults.filterTags.meal.4' },
  { value: '5', translationKey: 'vacation.oss.components.searchresults.filterTags.meal.5' },
];

type CreateMealPlansFilterOption = {
  defaultMealPlan: DefaultMealPlan;
  selectedValues: string[];
  accommodationMealPlan?: AccommodationMealPlan;
  translationDictionary: TranslationDictionary;
};
const createMealPlansFilterOption = ({
  defaultMealPlan,
  selectedValues,
  accommodationMealPlan,
  translationDictionary,
}: CreateMealPlansFilterOption): FilterDataOption => {
  const selected = selectedValues.includes(defaultMealPlan.value);
  // "Any" has no key of its own (255 holds the all-boards total), so it stays uncounted, not (0).
  const counter = defaultMealPlan.isDefault ? undefined : counterOf(accommodationMealPlan, defaultMealPlan.value);

  return {
    label: getTranslationServer(defaultMealPlan.translationKey, translationDictionary),
    selected,
    disabled: disabledAtZero(selected, counter),
    value: defaultMealPlan.value,
    counter,
    isDefault: !!defaultMealPlan.isDefault,
  };
};

type CreateMealPlansFilterOptions = {
  selectedValues: string[];
  accommodationMealPlan?: AccommodationMealPlan;
  translationDictionary: TranslationDictionary;
};

const createMealPlansFilterOptions = ({
  selectedValues,
  accommodationMealPlan,
  translationDictionary,
}: CreateMealPlansFilterOptions): FilterDataOption[] =>
  defaultMealPlans.map((defaultMealPlan) =>
    createMealPlansFilterOption({
      defaultMealPlan,
      selectedValues,
      accommodationMealPlan,
      translationDictionary,
    })
  );

type CreateMealPlansFilters = {
  searchParams: SearchParams;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};
export const createMealPlansFilters = ({
  searchParams,
  dynamicFiltersResponse,
  translationDictionary,
}: CreateMealPlansFilters): Filter[] => {
  const selectedValuesFromUrl = searchParams[URLParameters.MEAL]?.split(',') || [];
  const selectedValues = selectedValuesFromUrl.length > 0 ? selectedValuesFromUrl : [MEAL_PLAN_DEFAULT_VALUE];

  const options = createMealPlansFilterOptions({
    selectedValues,
    accommodationMealPlan: dynamicFiltersResponse?.accommodationMealPlan,
    translationDictionary,
  });

  const mealPlansFilter: Filter = {
    name: FILTER_NAME.MEAL_PLAN,
    type: FILTER_TYPE.MULTI_SELECT,
    data: {
      options,
      defaultValue: MEAL_PLAN_DEFAULT_VALUE,
    } as FilterDataMultiSelect,
  };

  return [...[mealPlansFilter]].filter((f) => !!f);
};
